import moment from "moment";
import { ItemData } from "types/item.api";

export const getRandomColorClass = () => {
  const classes = [
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
  ];
  const randomIndex = Math.floor(Math.random() * classes.length);
  return classes[randomIndex];
};

export const getTwoLetters = (str: string) => {
  if (!str) return "";

  const words = str.trim().split(/\s+/);

  if (words.length >= 2) {
    // First letter of the first two words
    return (words[0][0] + words[1][0]).toUpperCase();
  } else {
    // First two letters of the only word
    return words[0].slice(0, 2).toUpperCase();
  }
};

export const handleValidDate = (date: any) => {
  const date1 = moment(new Date(date)).format("DD MMM Y");
  return date1;
};

export const getBadgeColor = (status: string) => {
  let lowerCaseStatus = status.toLowerCase();
  if (lowerCaseStatus === "sent") {
    return "warning";
  } else if (lowerCaseStatus === "draft") {
    return "info";
  } else if (lowerCaseStatus === "overdue") {
    return "danger";
  } else if (lowerCaseStatus === "paid") {
    return "success";
  } else if (lowerCaseStatus === "approved") {
    return "success";
  } else if (lowerCaseStatus === "cancelled") {
    return "danger";
  } else if (lowerCaseStatus === "completed") {
    return "secondary";
  }
};

// Utility: generate pages with ellipses
export function getPageNumbers(current: number, total: number) {
  const delta = 2; // how many pages to show around current
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= total; i++) {
    if (
      i === 1 ||
      i === total ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i);
    }
  }

  for (let i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push("...");
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
}

export function timeFromNow(isoString: string): string {
  return moment(isoString).fromNow().replace("a day", "1 day");
}

export function calculateMargin(totalCost: number, sellingCost: number) {
  const marginAmount = sellingCost - totalCost;
  const marginPercentage =
    sellingCost === 0 ? 0 : (marginAmount / sellingCost) * 100;

  // helper to format: round to 2 decimals, remove trailing zeros
  const format = (value: number) => parseFloat(value.toFixed(2)); // removes .00 automatically

  return {
    marginAmount: format(marginAmount),
    marginPercentage: format(marginPercentage),
  };
}

export function calculateMarginData(items: ItemData[]) {
  let totalRevenue = 0;
  let totalCost = 0;

  const rows = items.map((item) => {
    const revenue = item.price * item.quantity;
    const cost = item.cost * item.quantity;
    const margin = revenue - cost;
    const marginPercent = revenue > 0 ? (margin / revenue) * 100 : 0;

    totalRevenue += revenue;
    totalCost += cost;

    return {
      name: item.name,
      revenue,
      cost,
      margin,
      marginPercent,
    };
  });

  const totalMargin = totalRevenue - totalCost;
  const totalMarginPercent =
    totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;

  return {
    rows,
    totals: {
      totalRevenue,
      totalCost,
      totalMargin,
      totalMarginPercent,
    },
  };
}

export function getLastNDaysRange(days = 30) {
  const formatDate = (date: Date) => date.toISOString().split("T")[0];
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - days);
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
}

export function getThisWeekDates() {
  const formatDate = (date: Date) => date.toISOString().split("T")[0];

  const today = new Date();
  const utcToday = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );

  const day = utcToday.getUTCDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const startDate = new Date(utcToday);
  startDate.setUTCDate(utcToday.getUTCDate() + diffToMonday);

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(utcToday),
  };
}

export function getCurrentWeekMonToSun() {
  const formatDate = (date: Date) => date.toISOString().split("T")[0];

  const today = new Date();

  // Create UTC midnight (prevents timezone shifting issues)
  const utcToday = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );

  const day = utcToday.getUTCDay(); // 0 = Sun, 1 = Mon ... 6 = Sat

  // Calculate Monday
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const startDate = new Date(utcToday);
  startDate.setUTCDate(utcToday.getUTCDate() + diffToMonday);

  // Sunday = Monday + 6
  const endDate = new Date(startDate);
  endDate.setUTCDate(startDate.getUTCDate() + 6);

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
}
